import { type ReactNode, useId, useMemo, useState } from 'react';

import {
  RadioGroupContext,
  type RadioGroupContextValue,
} from './RadioGroupContext';

export interface RadioGroupProps {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** Shared input name; auto-generated if omitted. */
  name?: string;
  children: ReactNode;
  className?: string;
  'aria-label'?: string;
}

/**
 * Groups [[Radio]] children (each keyed by `value`) into a single selection.
 * Controlled via `value`/`onChange` or uncontrolled via `defaultValue`.
 */
export const RadioGroup = ({
  value,
  defaultValue,
  onChange,
  name,
  children,
  className,
  ...rest
}: RadioGroupProps) => {
  const generatedName = useId();
  const groupName = name ?? generatedName;
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<string | undefined>(
    defaultValue
  );
  const currentValue = isControlled ? value : internalValue;

  const contextValue = useMemo<RadioGroupContextValue>(
    () => ({
      name: groupName,
      value: currentValue,
      onChange: (next) => {
        if (!isControlled) {
          setInternalValue(next);
        }
        onChange?.(next);
      },
    }),
    [groupName, currentValue, isControlled, onChange]
  );

  return (
    <RadioGroupContext.Provider value={contextValue}>
      <div role="radiogroup" className={className} {...rest}>
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
};
